import { useCallback, useEffect, useRef, useState } from "react";
import { createMonitoringAPICall } from "../../../utils/createMonitoringAPICall";
import { useAppStore } from "../../../../appStore";
import {
  isAuthTokenError,
  refreshAuthToken,
} from "../../../utils/refreshAuthToken";

export type WhepLiveStreamStatus =
  | "idle"
  | "loading"
  // Cold start is taking longer than a warm publish (~2-6s) — usually a
  // publish-only streaming deployment waking from scale-zero (pod schedule +
  // boot, ~15-45s). Consumers should show a "starting stream" affordance
  // instead of the generic connecting spinner copy.
  | "starting"
  | "playing"
  // The session/mint itself failed (streaming not enabled, bad session,
  // no camera id) — live view isn't offered here at all.
  | "unavailable"
  // The mint succeeded but the CAMERA's stream never came up or died
  // (cold-start retries exhausted, connection lost): a per-camera problem,
  // not a site-level one — consumers should say so.
  | "camera_offline"
  // Browser can't decode this camera's video format (e.g. H.265 without
  // hardware support) — hls.js BUFFER_INCOMPATIBLE_CODECS. Retrying can't
  // help; consumers should show a device-specific message.
  | "codec_unsupported"
  | "quota_reached"
  // The operator's account lacks the live-streaming permission (403 from the
  // mint) — e.g. streaming deliberately suspended for the account (ENG-1548).
  // Distinct from "unavailable" (a site/plan problem): retrying can't help
  // and the operator should be told it's an account-level state.
  | "not_permitted"
  // The mint was rejected for a stale/invalid access token and a refresh
  // could not recover it (ENG-1284). Distinct from "unavailable" (which is
  // a site/plan problem): the operator needs to reload/sign in again.
  | "session_expired"
  // Paused by the idle prompt (ENG-1132): no user activity for the configured
  // window, so the player was torn down (segments stop pulling → relay reaper
  // stops the publisher → metering stops). Consumers show "Still watching?";
  // resume goes through the full fresh-mint play() path.
  | "idle_paused";
export type StreamProtocol = "whep" | "hls";

interface StreamSessionResponse {
  whep_url: string;
  hls_url: string;
  token: string;
  /** CloudFront signed-URL params ("Policy=...&Signature=...&Key-Pair-Id=..."),
   * non-empty only when hls_url points at the CDN. Appended to the manifest
   * and every segment request (query params are excluded from the CDN cache
   * key, so this doesn't fragment the shared cache). */
  cdn_auth_params?: string;
  expires_at: number;
  session_id?: string;
  /** Monthly cap for this camera's plan. 0 = uncapped (the 24/7 opt-in /
   * internal lead); null = synthetic test path; absent on older backends.
   * Billing/overage only — the idle prompt (ENG-2149) ignores it. */
  cap_minutes?: number | null;
  /** True when the VIEWER bypasses the cap regardless of plan (staff /
   * cap-exempt groups). Billing only — the idle prompt ignores it. */
  cap_exempt?: boolean;
}

// Renew the publish lease while a viewer is actually connected. Must be shorter
// than the backend lease TTL (6 min) so a watched stream never lapses between
// renewals; when the player disconnects we stop renewing and release, so the
// connector stops publishing instead of pushing frames to nobody.
const KEEPALIVE_MS = 4 * 60 * 1000;
// Only "failed"/"closed" are terminal. "disconnected" is a TRANSIENT ICE state
// that usually recovers on its own within a second or two — tearing down on it
// is what made the alert-view stream flap on every network hiccup (StreamTest,
// which only logs the state, doesn't flap). We give "disconnected" a grace
// window (DISCONNECT_GRACE_MS) to recover before treating it as dead.
const DEAD_STATES = ["failed", "closed"];
const DISCONNECT_GRACE_MS = 8000;
// Publish-on-demand cold start: MediaMTX 404s until the publisher begins
// publishing. Warm connector: ~3-8s. Publish-only streaming deployment
// waking from scale-zero (unarmed sites, EDOCS 593657869): pod schedule +
// boot + boot-time lease read, ~15-45s. Retry player setup through that
// whole window (~50s) instead of hard-failing to "unavailable".
const START_RETRIES = 20;
const START_RETRY_DELAY_MS = 2500;
// After this many attempts (~8s), flip status to "starting" so the UI can
// say "starting the stream" instead of generic connecting copy.
const STARTING_STATUS_AFTER_ATTEMPT = 3;
// Mid-play resilience (PLT-344): a fatal AFTER playback started (publisher
// restart, disarm→arm publisher swap, LL-HLS sequence reset) cannot be cured
// by reloading the same manifest — recovery needs a fresh session MINT,
// which is also what re-evaluates publish routing (on an arm transition the
// warm connector only starts publishing when a mint/renewal routes a lease
// signal to it; manifest reloads never re-mint, so without this the stream
// died permanently on a 1s publisher blip). Bounded so a camera that dies
// mid-play still lands on camera_offline; the budget resets once playback
// resumes, so a recoverable stream keeps recovering.
const MID_PLAY_RESTARTS = 3;
const MID_PLAY_RESTART_DELAY_MS = 2000;

const waitForIceGathering = (pc: RTCPeerConnection, timeoutMs = 3000) =>
  new Promise<void>((resolve) => {
    if (pc.iceGatheringState === "complete") return resolve();
    const onState = () => {
      if (pc.iceGatheringState === "complete") {
        pc.removeEventListener("icegatheringstatechange", onState);
        resolve();
      }
    };
    pc.addEventListener("icegatheringstatechange", onState);
    setTimeout(resolve, timeoutMs);
  });

const isValidSession = (
  data: StreamSessionResponse | undefined | null,
  protocol: StreamProtocol
): data is StreamSessionResponse => {
  if (!data?.token) return false;
  return protocol === "whep" ? Boolean(data.whep_url) : Boolean(data.hls_url);
};

const isMonthlyLiveStreamQuotaError = (error: unknown): boolean => {
  const err = error as {
    response?: { status?: number; data?: { detail?: unknown } };
  };
  if (err?.response?.status !== 429) return false;
  const detail = err.response?.data?.detail;
  return (
    typeof detail === "string" &&
    detail.toLowerCase().includes("monthly live-stream quota reached")
  );
};

// User-level permission denial from the mint (ENG-1548): the operator's
// account lacks inframap.live_streaming — including the deliberate-suspension
// case. Matched on the backend's distinctive 403 detail, same pattern as the
// quota detection above.
const isLiveStreamNotPermittedError = (error: unknown): boolean => {
  const err = error as {
    response?: { status?: number; data?: { detail?: unknown } };
  };
  if (err?.response?.status !== 403) return false;
  const detail = err.response?.data?.detail;
  return (
    typeof detail === "string" &&
    detail.toLowerCase().includes("permission to view live streams")
  );
};

// The 403 is the backend telling us the cached permission is stale: the
// grant was revoked AFTER this tab's user/info fetch, and monitoring-center
// tabs live for weeks without a reload (ENG-1548 — 13 suspended operators
// kept seeing live affordances for 8+ days). Flip the store so every gated
// surface (alerts live panel, video wall) un-renders reactively instead of
// offering a button that can only 403, and keep the localStorage mirror in
// sync so the next boot doesn't resurrect it. user/info remains the source
// of truth: a re-grant shows up on the next login/page load as before.
const revokeCachedLiveStreamingPermission = () => {
  try {
    const { userPermissions, setUserPermissions } = useAppStore.getState();
    if (userPermissions?.live_streaming) {
      const next = { ...userPermissions, live_streaming: false };
      setUserPermissions(next);
      localStorage.setItem("userPermissions", JSON.stringify(next));
    }
  } catch {
    /* best-effort: the not_permitted status still renders regardless */
  }
};

// The quota 429 carries overage_available=true when the backend will accept a
// re-mint with overage_opt_in (the "continue for an additional fee" contract,
// ENG-719/721). Absent/false = a hard cap (e.g. plan without overage).
const isOverageAvailable = (error: unknown): boolean => {
  const err = error as {
    response?: { data?: { overage_available?: unknown } };
  };
  return err?.response?.data?.overage_available === true;
};

// idle_paused telemetry (ENG-1132). There is no frontend analytics sink in
// camera-ui today, so this is a structured console record plus a DOM event any
// future collector (New Relic browser agent, LD events) can subscribe to
// without touching the hook. Deliberately not routed through utils/logger,
// which is dev-only — we want this visible in prod consoles/session replays.
export const IDLE_PAUSED_EVENT = "actuate:live-view:idle_paused";
const logIdlePaused = (cameraId: string | null, sessionId: string | null) => {
  const detail = {
    event: "idle_paused",
    camera_id: cameraId,
    session_id: sessionId,
    at: new Date().toISOString(),
  };
  try {
    console.info("[live-view] idle_paused", detail);
    window.dispatchEvent(new CustomEvent(IDLE_PAUSED_EVENT, { detail }));
  } catch {
    /* telemetry is best-effort */
  }
};

export const useWhepLiveStream = (
  cameraId: string | null,
  enabled: boolean,
  videoRef: React.RefObject<HTMLVideoElement | null>,
  protocol: StreamProtocol = "whep",
  // HLS latency profile. Default true = low-latency (sub-second, small buffer) —
  // right for a single focused view. Pass false for a buffered profile (a few
  // seconds of cushion) — right for wall tiles, where a bursty source otherwise
  // starves the small low-latency buffer and the tile flickers. Ignored for WHEP.
  lowLatency = true,
  // Idle-prompt gate (ENG-1132). true = the owning view decided nobody is
  // watching: tear the player down (stop pulling, stop keepalives, release the
  // lease) and report "idle_paused"; flipping back to false re-runs the full
  // play() cycle (fresh mint), never a manifest reload.
  idlePaused = false
) => {
  const [status, setStatus] = useState<WhepLiveStreamStatus>("idle");
  // Monthly-cap overage consent (ENG-721). overageAvailable turns on when a
  // mint 429s with overage_available (the backend's "continue for a fee"
  // offer). continueWithOverage records consent for THIS camera view and
  // re-mints with overage_opt_in, which the keepalive then echoes so
  // renewals past the cap don't 429 and kill playback mid-session. Consent
  // deliberately resets when the camera changes or the view closes — every
  // over-cap viewing is an explicit choice, never ambient.
  const [overageAvailable, setOverageAvailable] = useState(false);
  const overageOptInRef = useRef(false);
  // ENG-1284: a mint rejected for a stale token gets ONE refresh-and-retry
  // per play cycle. Long idle pauses routinely outlive the ~1h access token,
  // so Resume used to land on "Live View not Available" instead of video.
  // Cleared on a successful mint, so every fresh cycle gets its own retry.
  const authRetriedRef = useRef(false);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const hlsRef = useRef<{ destroy: () => void } | null>(null);
  const playIdRef = useRef(0);
  const activeCameraRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  // Last minted session id, for idle_paused telemetry. Unlike sessionIdRef it
  // is NOT cleared by stop() — the idle effect runs after the previous effect's
  // cleanup has already torn the session down.
  const lastSessionIdRef = useRef<string | null>(null);
  const keepaliveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Pending "disconnected" grace timer (cleared if the connection recovers).
  const disconnectGraceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // In-flight cold-start retry timer (cleared by stop()).
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Current JWT, refreshed by the keepalive and read per-request by the HLS
  // xhrSetup so long HLS views survive the ~5-min token TTL (HLS authenticates
  // every segment; WHEP only needs the token once at setup).
  const tokenRef = useRef<string>("");
  // CloudFront signed-URL params for CDN-served sessions (empty = origin).
  // Refreshed with the token — the signature expires on the same TTL.
  const cdnParamsRef = useRef<string>("");
  // Mid-play restart machinery (PLT-344). playedRef: playback was achieved in
  // the current play cycle (manifest parsed / first WHEP track) — only then
  // does a fatal qualify for a full re-mint restart instead of the cold-start
  // reload loop. restartsRef: bounded budget, reset when playback resumes.
  // playRef: late-bound play() so the restart timer (and playHls/playWhep,
  // defined before play) can trigger a full fresh-mint cycle.
  const playedRef = useRef(false);
  const restartsRef = useRef(0);
  const playRef = useRef<() => void>(() => {});

  // Tell the backend the viewer is gone so the connector stops publishing now,
  // instead of tailing out the lease (~6 min). Best-effort fire-and-forget.
  const releaseLease = useCallback((camId: string) => {
    try {
      createMonitoringAPICall("")
        .delete(`streaming/stream-session/${camId}/`)
        .catch(() => {});
    } catch {
      /* best-effort */
    }
  }, []);

  const stop = useCallback(() => {
    if (keepaliveRef.current) {
      clearInterval(keepaliveRef.current);
      keepaliveRef.current = null;
    }
    if (disconnectGraceRef.current) {
      clearTimeout(disconnectGraceRef.current);
      disconnectGraceRef.current = null;
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (activeCameraRef.current) {
      releaseLease(activeCameraRef.current);
      activeCameraRef.current = null;
    }
    sessionIdRef.current = null;
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.removeAttribute("src");
      videoRef.current.load();
    }
  }, [videoRef, releaseLease]);

  // Full recovery from a mid-play fatal (PLT-344): tear down, then re-run the
  // whole play() cycle — fresh mint, fresh URLs/token, fresh player. Returns
  // true when a restart was scheduled; false = budget spent or playback never
  // started (caller falls through to its terminal status). The timer is set
  // AFTER stop() (stop clears retryTimerRef) and playId-gated so a disable
  // or a newer play cycle orphans it safely.
  const attemptMidPlayRestart = useCallback((): boolean => {
    if (!playedRef.current) return false;
    if (restartsRef.current >= MID_PLAY_RESTARTS) return false;
    restartsRef.current += 1;
    stop();
    setStatus("loading");
    const pid = playIdRef.current;
    retryTimerRef.current = setTimeout(() => {
      if (pid === playIdRef.current) playRef.current();
    }, MID_PLAY_RESTART_DELAY_MS);
    return true;
  }, [stop]);

  // setTimeout-based sleep whose handle lives in retryTimerRef, so stop() can
  // cancel an in-flight cold-start retry.
  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        retryTimerRef.current = setTimeout(resolve, ms);
      }),
    []
  );

  // Renew the lease every KEEPALIVE_MS, but ONLY while the player is still
  // connected — the "gate". A failed/closed connection (or a destroyed hls
  // player) stops renewing and releases, so the lease lapses promptly.
  const startKeepalive = useCallback(
    (camId: string) => {
      if (keepaliveRef.current) clearInterval(keepaliveRef.current);
      keepaliveRef.current = setInterval(() => {
        const pc = pcRef.current;
        const connected =
          (pc && !DEAD_STATES.includes(pc.connectionState)) ||
          hlsRef.current != null;
        if (!connected) {
          if (attemptMidPlayRestart()) return;
          stop();
          setStatus("camera_offline");
          return;
        }
        createMonitoringAPICall("")
          .post(`streaming/stream-session/${camId}/`, {
            session_id: sessionIdRef.current,
            ...(overageOptInRef.current ? { overage_opt_in: true } : {}),
          })
          .then((resp) => {
            // Refresh the JWT (HLS xhrSetup reads tokenRef per request) and keep
            // the billing session continuous across renewals.
            if (resp?.data?.token) tokenRef.current = resp.data.token;
            cdnParamsRef.current = resp?.data?.cdn_auth_params || "";
            if (resp?.data?.session_id) {
              sessionIdRef.current = resp.data.session_id;
              lastSessionIdRef.current = resp.data.session_id;
            }
          })
          .catch(() => {});
      }, KEEPALIVE_MS);
    },
    [stop, attemptMidPlayRestart]
  );

  const playWhep = useCallback(
    async (whepUrl: string, token: string, playId: number) => {
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.addTransceiver("video", { direction: "recvonly" });
      pc.addTransceiver("audio", { direction: "recvonly" });
      pc.ontrack = (e) => {
        if (playId !== playIdRef.current) return;
        if (videoRef.current && e.streams[0]) {
          videoRef.current.srcObject = e.streams[0];
          playedRef.current = true;
          restartsRef.current = 0;
          setStatus("playing");
        }
      };
      // The viewer's connection dropped (closed tab mid-stream, network loss,
      // ICE failure): stop + release so the connector isn't left publishing to
      // nobody until the lease lapses. But "disconnected" is transient — ICE
      // usually recovers — so we ride it out for DISCONNECT_GRACE_MS and only
      // tear down if it hasn't returned to "connected" by then (or it goes
      // straight to a terminal state).
      pc.onconnectionstatechange = () => {
        if (playId !== playIdRef.current) return;
        const state = pc.connectionState;
        // Recovered (or still (re)connecting): cancel any pending grace timer.
        if (
          state === "connected" ||
          state === "connecting" ||
          state === "new"
        ) {
          if (disconnectGraceRef.current) {
            clearTimeout(disconnectGraceRef.current);
            disconnectGraceRef.current = null;
          }
          return;
        }
        // Terminal — recover with a fresh mint if we were playing, else tear
        // down now.
        if (DEAD_STATES.includes(state)) {
          if (attemptMidPlayRestart()) return;
          stop();
          setStatus("camera_offline");
          return;
        }
        // "disconnected" — give ICE a chance to recover before tearing down.
        if (state === "disconnected" && !disconnectGraceRef.current) {
          disconnectGraceRef.current = setTimeout(() => {
            disconnectGraceRef.current = null;
            if (playId !== playIdRef.current) return;
            if (pcRef.current?.connectionState !== "connected") {
              if (attemptMidPlayRestart()) return;
              stop();
              setStatus("camera_offline");
            }
          }, DISCONNECT_GRACE_MS);
        }
      };

      const offer = await pc.createOffer();
      if (playId !== playIdRef.current) return;

      await pc.setLocalDescription(offer);
      await waitForIceGathering(pc);

      if (playId !== playIdRef.current) return;

      // POST the SDP offer, retrying through the publish-on-demand cold start
      // (MediaMTX 404s until the connector starts publishing the camera).
      for (let attempt = 1; attempt <= START_RETRIES; attempt++) {
        const whepResp = await fetch(
          `${whepUrl}?jwt=${encodeURIComponent(token)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/sdp" },
            body: pc.localDescription!.sdp,
          }
        );

        if (playId !== playIdRef.current) return;

        if (whepResp.ok) {
          const answerSdp = await whepResp.text();
          if (playId !== playIdRef.current) return;
          await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
          return;
        }

        // Cold start: retry on 404 until the path is publishing.
        if (whepResp.status === 404 && attempt < START_RETRIES) {
          if (attempt >= STARTING_STATUS_AFTER_ATTEMPT) {
            setStatus("starting");
          }
          await sleep(START_RETRY_DELAY_MS);
          if (playId !== playIdRef.current) return;
          continue;
        }

        stop();
        setStatus("camera_offline");
        return;
      }
    },
    [stop, videoRef, sleep, attemptMidPlayRestart]
  );

  const playHls = useCallback(
    async (hlsUrl: string, token: string, playId: number) => {
      if (!videoRef.current) {
        setStatus("unavailable");
        return;
      }

      // CDN sessions additionally carry the CloudFront signature on every
      // request (camera-ui is same-site with the CDN, but URL params are the
      // one mechanism proven across all viewers — cookies stay unused).
      const applyCdnParams = (u: URL) => {
        const raw = cdnParamsRef.current;
        if (!raw) return;
        new URLSearchParams(raw).forEach((value, key) => {
          u.searchParams.set(key, value);
        });
      };
      const initialUrl = new URL(hlsUrl);
      initialUrl.searchParams.set("jwt", token);
      applyCdnParams(initialUrl);
      const url = initialUrl.toString();

      const Hls = (await import("hls.js")).default;

      if (playId !== playIdRef.current) return;

      if (Hls.isSupported()) {
        // One attempt at building + attaching the player; retries through the
        // publish-on-demand cold start (a fatal error is usually the manifest
        // 404 before the connector begins publishing).
        const startAttempt = (attempt: number) => {
          if (playId !== playIdRef.current || !videoRef.current) return;
          const hls = new Hls({
            // Latency profile: low-latency (small buffer, sub-second) for a
            // single focused view; buffered for wall tiles, where a bursty
            // source starves the LL buffer and the tile flickers.
            lowLatencyMode: lowLatency,
            ...(lowLatency
              ? {}
              : {
                  // Buffered profile: keep several segments of cushion behind
                  // the live edge so bursty delivery is absorbed rather than
                  // starving playback (costs a few seconds of latency — fine
                  // for a monitoring grid).
                  liveSyncDurationCount: 6,
                  maxBufferLength: 30,
                }),
            backBufferLength: 120,
            // Gap tolerance for a bursty source: jump buffer holes up to 1s
            // and keep nudging instead of escalating to a fatal error, so a
            // rebuffer stall doesn't tear the stream down.
            maxBufferHole: 1.0,
            nudgeMaxRetry: 10,
            // Inject the CURRENT token on every manifest/segment request. The
            // keepalive re-mints it every ~4 min, so an HLS view survives the
            // ~5-min JWT TTL without reloading the source (otherwise segment
            // requests 401 once the original token expires and playback dies).
            xhrSetup: (xhr: XMLHttpRequest, requestUrl: string) => {
              try {
                const u = new URL(requestUrl);
                u.searchParams.set("jwt", tokenRef.current);
                applyCdnParams(u);
                xhr.open("GET", u.toString(), true);
              } catch {
                /* malformed URL — let hls.js open it unmodified */
              }
            },
          });
          hlsRef.current = hls;

          hls.on(Hls.Events.ERROR, (_evt, data) => {
            if (playId !== playIdRef.current) return;
            // Non-fatal (fragGap, bufferStalled, …): hls.js recovers/nudges.
            if (!data.fatal) return;
            hls.destroy();
            if (hlsRef.current === hls) hlsRef.current = null;
            // Decode capability is a device property, not a stream failure —
            // retrying can't help (H.265 camera on a browser without HEVC
            // support). Skip the retry window and name the real problem.
            if (
              data.details != null &&
              data.details ===
                Hls.ErrorDetails?.BUFFER_INCOMPATIBLE_CODECS_ERROR
            ) {
              stop();
              setStatus("codec_unsupported");
              return;
            }
            // Fatal after playback had started: the manifest-reload loop below
            // can't recover from a publisher restart or an arm-transition swap
            // — only a fresh mint can (PLT-344). Skip straight to a full
            // restart while budget remains.
            if (attemptMidPlayRestart()) return;
            if (attempt < START_RETRIES) {
              if (attempt >= STARTING_STATUS_AFTER_ATTEMPT) {
                setStatus("starting");
              }
              retryTimerRef.current = setTimeout(
                () => startAttempt(attempt + 1),
                START_RETRY_DELAY_MS
              );
            } else {
              stop();
              setStatus("camera_offline");
            }
          });

          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (playId !== playIdRef.current) return;
            playedRef.current = true;
            restartsRef.current = 0;
            videoRef.current?.play().catch(() => {
              stop();
              setStatus("camera_offline");
            });
            setStatus("playing");
          });

          hls.loadSource(url);
          hls.attachMedia(videoRef.current);
        };
        startAttempt(1);
      } else if (
        videoRef.current.canPlayType("application/vnd.apple.mpegurl")
      ) {
        videoRef.current.src = url;
        await videoRef.current.play().catch(() => {
          stop();
          setStatus("camera_offline");
        });
        if (playId !== playIdRef.current) return;
        setStatus("playing");
      } else {
        setStatus("unavailable");
      }
    },
    [stop, videoRef, lowLatency, attemptMidPlayRestart]
  );

  const play = useCallback(async () => {
    const playId = ++playIdRef.current;
    stop();
    // New cycle: playback not yet achieved. restartsRef deliberately persists
    // across restarts (it IS the cross-cycle budget) and resets on playback.
    playedRef.current = false;
    setStatus("loading");

    if (!cameraId) {
      setStatus("unavailable");
      return;
    }

    try {
      const api = createMonitoringAPICall("");
      const sessResp = await api.post<StreamSessionResponse>(
        `streaming/stream-session/${cameraId}/`,
        overageOptInRef.current ? { overage_opt_in: true } : {}
      );

      if (playId !== playIdRef.current) return;

      if (!isValidSession(sessResp.data, protocol)) {
        setStatus("unavailable");
        return;
      }

      authRetriedRef.current = false;
      const { whep_url, hls_url, token } = sessResp.data;
      tokenRef.current = token;
      cdnParamsRef.current = sessResp.data.cdn_auth_params || "";

      // Lease is live: track it for release + start renewing while connected.
      activeCameraRef.current = cameraId;
      sessionIdRef.current = sessResp.data.session_id ?? null;
      lastSessionIdRef.current = sessionIdRef.current;
      startKeepalive(cameraId);

      if (protocol === "whep") {
        await playWhep(whep_url, token, playId);
      } else {
        await playHls(hls_url, token, playId);
      }
    } catch (error) {
      if (playId !== playIdRef.current) return;
      stop();
      if (isMonthlyLiveStreamQuotaError(error)) {
        setOverageAvailable(isOverageAvailable(error));
        setStatus("quota_reached");
      } else if (isLiveStreamNotPermittedError(error)) {
        setStatus("not_permitted");
        revokeCachedLiveStreamingPermission();
      } else if (isAuthTokenError(error)) {
        // Stale access token (typically a view idle-paused past the token
        // TTL): refresh once and re-run the whole cycle — a fresh mint with
        // the new token, same as Resume. Only if that fails (or a retried
        // mint is rejected again) is the session really expired.
        if (!authRetriedRef.current) {
          authRetriedRef.current = true;
          const refreshed = await refreshAuthToken();
          if (playId !== playIdRef.current) return;
          if (refreshed) {
            void playRef.current();
            return;
          }
        }
        setStatus("session_expired");
      } else {
        setStatus("unavailable");
      }
    }
  }, [cameraId, protocol, stop, playWhep, playHls, startKeepalive]);

  // Late-bind so attemptMidPlayRestart (defined before play) always invokes
  // the current play cycle.
  useEffect(() => {
    playRef.current = play;
  }, [play]);

  // Operator accepted the overage offer: record consent for this view and
  // re-mint. Consent survives background-tab recovery and mid-play restarts
  // (same viewing), but not a camera switch or closing the view (below).
  const continueWithOverage = useCallback(() => {
    overageOptInRef.current = true;
    setOverageAvailable(false);
    play();
  }, [play]);

  // Overage consent is per camera view — a different camera or a re-opened
  // view must re-prompt rather than silently keep billing.
  useEffect(() => {
    overageOptInRef.current = false;
    setOverageAvailable(false);
  }, [cameraId, enabled]);

  useEffect(() => {
    if (!enabled) {
      playIdRef.current += 1;
      stop();
      setStatus("idle");
      return;
    }
    if (idlePaused) {
      logIdlePaused(cameraId, lastSessionIdRef.current);
      playIdRef.current += 1;
      stop();
      setStatus("idle_paused");
      return;
    }

    play();
    return () => {
      playIdRef.current += 1;
      stop();
    };
  }, [enabled, idlePaused, cameraId, protocol, play, stop]);

  // Recover after the tab was backgrounded. Browsers throttle hidden tabs, so
  // the player stops pulling (HLS especially) and the stream is torn down /
  // released while we're away. None of the deps above change when the tab
  // returns, so without this a tile stays stuck on "unavailable". On return to a
  // visible tab, re-acquire if the stream isn't currently alive.
  useEffect(() => {
    // While idle-paused the teardown is deliberate — returning to the tab must
    // not silently re-mint; the operator clicks Resume.
    if (!enabled || idlePaused) return;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const pcAlive =
        !!pcRef.current && !DEAD_STATES.includes(pcRef.current.connectionState);
      const hlsAlive = hlsRef.current != null;
      if (!pcAlive && !hlsAlive) {
        play(); // stream went down while backgrounded -> re-establish
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [enabled, idlePaused, play]);

  return { status, stop, overageAvailable, continueWithOverage };
};
